<script>
import { startCase, uniqueId } from 'lodash'
import { toJpeg } from 'html-to-image'
import Chart from '../views/Chart'

export default {
  name: 'DownloadForm',
  components: {
    Chart
  },
  props: {
    title: {
      type: String
    },
    socialMode: {
      type: Boolean
    },
    chart: {
      type: Object
    }
  },
  filters: {
    startCase
  },
  data () {
    return {
      height: null,
      width: 660,
      key: null
    }
  },
  methods: {
    buildLink (href, network, newTab = false) {
      const link = document.createElement('a')
      link.href = href
      if (newTab) {
        link.target = '_blank'
      } else {
        link.download = `${this.title} (${startCase(network)}).jpg`
      }
      return link
    },
    async download ([width, height], network, newTab = false) {
      await this.resize(width, height)
      try {
        const href = await this.screenshot()
        this.buildLink(href, network, newTab).click()
        this.key = null
      } catch (_) {
        const title = 'Something went wrong'
        const options = { title, solid: true, variant: 'danger' }
        this.$bvToast.toast('Unable to generate the image.', options)
      }
    },
    async downloadWithLoader ([width, height], network, newTab = false) {
      this.$wait.start(this.networkLoader(network))
      await this.download([width, height], network, newTab)
      this.$wait.end(this.networkLoader(network))
    },
    isNetworkLoading (network) {
      return this.$wait.is(this.networkLoader(network))
    },
    networkLoader (network) {
      return `${this.formId}-generate-screenshot-for-${network}`
    },
    resize (width, height) {
      this.width = width
      this.height = height
      this.key = uniqueId('download-form-chart-')
      return this.$nextTick()
    },
    async screenshot () {
      await new Promise(resolve => setTimeout(resolve, 1000))
      const selector = '.download-form__component > *'
      const element = this.$el.querySelector(selector)
      return toJpeg(element)
    }
  },
  computed: {
    networkSizes () {
      return {
        twitter: [1200, 675],
        facebook: [1200, 628],
        instagram: [1080, 566],
        linkedin: [1104, 736]
      }
    },
    componentStyle () {
      return {
        width: `${this.width}px`,
        height: `${this.height}px`
      }
    },
    socialModeAccessor: {
      get () {
        return this.socialMode
      },
      set (value) {
        this.$emit('update:socialMode', value)
      }
    },
    formId () {
      return uniqueId('download-form-')
    }
  }
}
</script>

<template>
  <div class="download-form">
    <div class="container-fluid">
      <b-form-checkbox v-model="socialModeAccessor" class="my-4">
        <span class="font-weight-bold">
          Social mode
        </span>
        <span class="text-muted">(dark background)</span>
      </b-form-checkbox>
      <div v-for="(sizes, name) in networkSizes" :key="name" class="d-flex align-items-center justify-content-between mb-2 pl-2 border">
        <span class="font-weight-bolder">
          <fa :icon="['fab', name.split('-').shift()]" class="mr-2" fixed-with />
          {{ name | startCase }}
          <span class="font-weight-normal text-muted ml-1">
            ({{ sizes.join('x') }})
          </span>
        </span>
        <b-overlay :show="isNetworkLoading(name)" spinner-small>
          <b-btn @click.exact="downloadWithLoader(sizes, name)" @click.ctrl="downloadWithLoader(sizes, name, true)" :disable="isNetworkLoading(name)">
            <fa icon="download" class="mr-1" />
            JPG
          </b-btn>
        </b-overlay>
      </div>
      <div class="download-form__component" :style="componentStyle" :key="key">
        <chart
          :chart="chart"
          :height="height"
          :social-mode="socialMode"
          :style="componentStyle"
          no-footer
          v-if="key" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @import '../variables.scss';

  .download-form {

    &__component {
      pointer-events: none;
      position: absolute;
      left: -9999px;
      top: -9999px;
      overflow: hidden;

      & > .chart {
        background: white;
        font-size: 1.4rem;

        &--social-mode {
          background: $secondary;
        }
      }
    }
  }
</style>
